import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateUserPreferences } from '@/lib/user-preferences-service';
import type { LanguagePreference } from '@/lib/user-preferences';
import { cn } from '@/lib/utils';

export const LanguageToggle = () => {
  const { i18n } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [currentLang, setCurrentLang] = useState<LanguagePreference>('pl');

  // Prevent hydration mismatch and ensure fallback to 'pl'
  useEffect(() => {
    setMounted(true);
    // Ensure fallback to 'pl' if language is not set or invalid
    const lang = (i18n.language as LanguagePreference) || 'pl';
    if (lang !== 'pl' && lang !== 'ua') {
      i18n.changeLanguage('pl');
      setCurrentLang('pl');
    } else {
      setCurrentLang(lang);
    }
  }, [i18n.language]);

  const setLanguage = async (lang: LanguagePreference) => {
    // Don't do anything if language is already active
    if (lang === currentLang) return;
    
    await i18n.changeLanguage(lang);
    setCurrentLang(lang);
    
    // Save to database
    try {
      await updateUserPreferences({ language: lang });
    } catch (error) {
      console.error('[LanguageToggle] Error saving language preference:', error);
    }
  };

  if (!mounted) {
    return (
      <div className="flex items-center gap-1 rounded-lg bg-transparent dark:bg-[#2C3241] p-1">
        <span className="px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400">PL</span>
        <span className="px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400">UA</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 rounded-lg bg-card dark:bg-transparent border border-border/50 dark:border-border/30 p-1 shadow-sm">
      <button
        onClick={() => setLanguage('pl')}
        className={cn(
          "px-3 py-1.5 text-sm font-medium transition-all duration-200 rounded",
          currentLang === 'pl'
            ? "text-gray-900 dark:text-white bg-primary/10 dark:bg-primary text-primary-foreground dark:text-primary-foreground shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-accent"
        )}
        aria-label="Wybierz język polski"
      >
        PL
      </button>
      <button
        onClick={() => setLanguage('ua')}
        className={cn(
          "px-3 py-1.5 text-sm font-medium transition-all duration-200 rounded",
          currentLang === 'ua'
            ? "text-gray-900 dark:text-white bg-primary/10 dark:bg-primary text-primary-foreground dark:text-primary-foreground shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-accent"
        )}
        aria-label="Wybierz język ukraiński"
      >
        UA
      </button>
    </div>
  );
};
